import { EditorProps } from "@tiptap/pm/view";
import { DateTime } from "luxon";
import rehypeParse from "rehype-parse";
import rehypeRemark from "rehype-remark";
import remarkStringify from "remark-stringify";
import { unified } from "unified";
import { z } from "zod";

import { getHTML } from "@/common/utils";
import { invariant } from "@/common/utils/invariant";
import {
  CommodityTo,
  ReportCommodityToRecommendation,
  ReportTo,
} from "@/generated/api/news";

import { uploadImage } from "./editor.api";

type FormValues = {
  headline: string;
  dateFrom: string;
  dateTo: string;
  summary: string;
  language: string;
  commodities: {
    commodityId: number;
    headline: string;
    summary: string;
    recommendation: string;
    recommendationText: string;
  }[];
  // used for edit mode
  imagePreview?: {
    guid: string;
    url: string;
  };
};

type FormValuesWithImage = FormValues & { image?: File };

const commoditySchema = z.object({
  commodityId: z.int(),
  headline: z.string(),
  recommendation: z.enum(
    Object.keys(ReportCommodityToRecommendation) as [
      keyof typeof ReportCommodityToRecommendation,
    ],
  ),
  recommendationText: z.string(),
  summary: z.string(),
});

const reportFormSchema = z.object({
  commodities: z.array(commoditySchema).min(1),
  dateFrom: z.string().regex(/\d{4}-\d{2}-\d{2}/),
  dateTo: z.string().regex(/\d{4}-\d{2}-\d{2}/),
  headline: z.string(),
  image: z.file().optional(),
  language: z.string(),
  summary: z.string().optional(),
});

const reportPayloadSchema = reportFormSchema.omit({ image: true }).extend({
  imageId: z.string().optional(),
});

type ReportPayload = z.infer<typeof reportPayloadSchema>;

const getReportPayload = async (data: FormValuesWithImage) => {
  const { image, ...rest } = data;
  const payload = structuredClone(rest) as ReportPayload;

  if (image && image.name.length > 0) {
    const uploaded = await uploadImage(image);
    payload.imageId = uploaded.guid;
  }

  const safePayload = reportPayloadSchema.parse(payload);

  for (const commodity of safePayload.commodities) {
    commodity.summary = await getMarkdownPayload(commodity.summary);
    commodity.recommendationText = await getMarkdownPayload(
      commodity.recommendationText,
    );
  }

  return safePayload;
};

const getMarkdownPayload = async (input: string) => {
  const html = getHTML(input);
  const htmlWithImages = await getHTMLWithImages(html);
  const markdown = getMarkdown(htmlWithImages);

  return markdown;
};

const getMarkdown = (html: Document): string => {
  const markdown = unified()
    .use(rehypeParse)
    .use(rehypeRemark)
    .use(remarkStringify)
    .processSync(html.body.innerHTML);

  return String(markdown);
};

const getHTMLWithImages = async (doc: Document) => {
  const images = doc.body.querySelectorAll("img");

  for (const image of images) {
    if (!image.src.startsWith("blob:")) {
      // do no process images coming from the internet
      continue;
    }
    const blob = await (await fetch(image.src)).blob();
    const uploaded = await uploadImage(blob);
    image.src = uploaded.url;
  }

  return doc;
};

// https://www.codemzy.com/blog/tiptap-drag-drop-image
const handleImageDrop: EditorProps["handleDrop"] = (
  view,
  event,
  _slice,
  moved,
) => {
  if (
    !moved &&
    event.dataTransfer &&
    event.dataTransfer.files &&
    event.dataTransfer.files[0]
  ) {
    const file = event.dataTransfer.files[0];
    if (file.type.startsWith("image/")) {
      const img = document.createElement("img");
      img.src = window.URL.createObjectURL(file);
      const { schema } = view.state;
      const coordinates = view.posAtCoords({
        left: event.clientX,
        top: event.clientY,
      });
      const node = schema.nodes.image.create({ src: img.src });
      // eslint-disable-next-line lingui/no-unlocalized-strings
      invariant(coordinates, "coordinates are not defined");
      const transaction = view.state.tr.insert(coordinates.pos, node);

      return view.dispatch(transaction);
    }
    return true;
  }
  return false;
};

const getInitialValues = (
  from: ReportTo | undefined,
  commodities: CommodityTo[],
): FormValues => {
  const values: FormValues = {
    commodities: [],
    dateFrom: from?.dateFrom ?? DateTime.now().toISODate(),
    dateTo: from?.dateTo ?? DateTime.now().plus({ days: 7 }).toISODate(),
    headline: from?.headline ?? "",
    imagePreview: from?.image,
    language: from?.language ?? "",
    summary: from?.summary ?? "",
  };

  commodities.forEach((commodity) => {
    const reportCommodity = from?.commodities.find(
      (c) => c.commodity.id === commodity.id,
    );
    values.commodities.push({
      commodityId: commodity.id,
      headline: reportCommodity?.headline ?? "",
      recommendation: reportCommodity?.recommendation ?? "",
      recommendationText: reportCommodity?.recommendationText ?? "",
      summary: reportCommodity?.summary ?? "",
    });
  });

  return values;
};

export { getInitialValues, getReportPayload, handleImageDrop };
export type { FormValues, FormValuesWithImage, ReportPayload };
